import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface LogoMarkProps {
    className?: string
    tone?: 'brand' | 'light' | 'dark'
}

interface LogoProps extends LogoMarkProps {
    showDescriptor?: boolean
}

/**
 * The Ura mark: a bridge whose two piers and arch form the letter U,
 * crossed by the deck, with a signal point where the two sides meet.
 */
export const LogoMark: FC<LogoMarkProps> = ({ className, tone = 'brand' }): ReactNode => {
    const stroke = tone === 'light' ? '#ffffff' : tone === 'dark' ? '#0d1526' : 'currentColor'

    return (
        <svg
            viewBox='0 0 32 32'
            fill='none'
            aria-hidden='true'
            className={cn('size-8 shrink-0', className)}
        >
            <path
                d='M9 4.5v11.25a7 7 0 0 0 14 0V4.5'
                stroke={stroke}
                strokeWidth='3.2'
                strokeLinecap='round'
            />
            <path
                d='M3.5 12h25'
                stroke={stroke}
                strokeWidth='2'
                strokeLinecap='round'
                opacity='0.55'
            />
            <circle cx='16' cy='27.6' r='2.1' fill='#2fd0e4' />
        </svg>
    )
}

export const Logo: FC<LogoProps> = ({ className, tone = 'brand', showDescriptor = true }): ReactNode => {
    return (
        <span className={cn('inline-flex items-center gap-2.5', className)}>
            <LogoMark tone={tone} className={tone === 'brand' ? 'text-primary' : undefined} />

            <span className='flex items-baseline gap-1.5 leading-none'>
                <span className={cn('text-[1.35rem] font-semibold tracking-[-0.03em]', tone === 'light' ? 'text-white' : 'text-foreground')}>
                    ura
                </span>

                {showDescriptor && (
                    <span className={cn('text-[0.68rem] font-medium uppercase tracking-[0.22em]', tone === 'light' ? 'text-white/65' : 'text-muted-foreground')}>
                        Advisory
                    </span>
                )}
            </span>
        </span>
    )
}
